import { h, Fragment } from 'preact'
import { useRef, useEffect, useState } from 'preact/compat'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { handleApplePay } from './handler'
import ErrorMessage from '../../Common/ErrorMessage'
import { useTranslations } from '../../../hooks/useTranslations'
import {
  dispatchMFEAdobeEvent,
  dispatchMFEEvent,
} from '../../../helpers/events'
import { DataDog } from '../../../datadog/index'
import useFailureRedirect from '../../../hooks/useFailureRedirect'
import useAuthoriseError from '../../../hooks/useAuthoriseError'
import InfoMessage from '../../Common/InfoMessage'
import {
  formatAsCurrency,
  isMobile,
  sprintf,
  toApplePayLocale,
} from '../../../helpers/common'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'
import './styles.scss'

export const errorHandler =
  (hasRedirect, setError, onAuthoriseResponse) =>
  (err, responseStatus, responseData) => {
    if (hasRedirect) {
      return
    }

    onAuthoriseResponse(responseStatus, responseData)

    setError(err)
  }

function ApplePay({ children, handleSubmit, showPaymentConfirmation }) {
  const l10n = useTranslations()
  const [error, setError] = useState('')

  useFailureRedirect()
  const handleAuthoriseError = useAuthoriseError()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const pageState = useSelector(pageStateSelector)

  const formatAmount = formatAsCurrency(
    transactionDetails.currency,
    transactionDetails.culture,
  )

  const currentAmount = formatAmount(
    transactionDetails?.overrideAmount
      ? transactionDetails.overrideAmount
      : transactionDetails.amount,
  )

  const handleClickApplePay = () => {
    DataDog.info('Apple Pay button was clicked')
    dispatchMFEEvent('tui-button.payment-btn-clicked', {
      paymentMethod: 'ApplePay',
    })
    dispatchMFEAdobeEvent('Pay-ApplePay')

    if (pageState.isPaymentBlocked) {
      setError(l10n.t('payment-btn-error'))
      return
    }
    setError('')

    handleSubmit(({ notes }) => {
      handleApplePay(
        { ...transactionDetails, ...notes },
        errorHandler(
          Boolean(transactionDetails.failureUrl),
          setError,
          handleAuthoriseError,
        ),
        l10n,
      )
    })()
  }

  /**
   * ApplePay button is also MFE, and has its own restrictions regarding usage in other MFEs.
   * That's why we need to render it in the MFE Payments slots, so the
   * Apple Pay button will be rendered correctly
   */
  const btnRef = useRef(null)
  const SLOT_NAME = 'tui-payments-apple-pay-button'

  useEffect(() => {
    const btn = document.createElement('apple-pay-button')
    btn.setAttribute('buttonstyle', 'black')
    btn.setAttribute('type', 'plain')
    btn.setAttribute('locale', toApplePayLocale(transactionDetails.culture))
    btn.setAttribute('id', 'apple-pay-button')
    btn.setAttribute('slot', SLOT_NAME)
    btn.style.cssText = `
      --apple-pay-button-width: ${isMobile() ? '100%' : '110px'};
      --apple-pay-button-height: 40px;
      --apple-pay-button-border-radius: 3px;
      --apple-pay-button-padding: 0px 0px;
      --apple-pay-button-box-sizing: border-box;
    `

    window.tuiPaymentsMFEInstance.appendChild(btn)
    btnRef.current = btn

    return () => {
      window.tuiPaymentsMFEInstance.removeChild(btn)
    }
  }, [])

  // Keep the button locale in sync when the transaction culture changes
  // after mount (e.g. via MFE hooks). Updating the attribute in place avoids
  // recreating the button and re-attaching its click listener.
  useEffect(() => {
    btnRef.current?.setAttribute(
      'locale',
      toApplePayLocale(transactionDetails.culture),
    )
  }, [transactionDetails.culture])

  useEffect(() => {
    const { current } = btnRef

    current.addEventListener('click', handleClickApplePay)

    if (!pageState.isPaymentBlocked) {
      setError('')
    }

    return () => current.removeEventListener('click', handleClickApplePay)
  }, [btnRef, transactionDetails.overrideAmount, pageState.isPaymentBlocked])

  return (
    <>
      {children}
      {showPaymentConfirmation && (
        <InfoMessage
          message={sprintf(
            l10n.t(`You're about to pay %s with Apple Pay`),
            currentAmount,
          )}
          data-testid="apple-pay-info-message"
        />
      )}
      <ErrorMessage
        isShown={Boolean(error)}
        data-testid="applepay-error"
        message={error}
      />
      <div className="button-wrapper" data-testid="applepay-button">
        <slot name={SLOT_NAME} />
      </div>
    </>
  )
}

ApplePay.propTypes = {
  children: PropTypes.element,
  handleSubmit: PropTypes.func,
  showPaymentConfirmation: PropTypes.bool,
}

export default ApplePay
